<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import Checkbox from '@/components/Shared/Input/Checkbox/Checkbox.vue';
import ChevronDownIcon from '@/assets/svg/chevron-down-16.svg?component';
import { ApplicationBulkActions } from '@/core/conversations/conversation-index/conversation-index.type';

const props = withDefaults(
  defineProps<{
    areAllSelected: boolean;
    areSomeSelected: boolean;
    isSendingBulkAction: boolean;
    /**
     * Hard-disables the whole control (checkbox, menu activator, and menu).
     * Used while the account-wide search suspends browsing controls — the
     * menu teleports outside the suspended wrapper, so a wrapper-level
     * pointer-events/inert treatment alone cannot cover it (ONE-6912).
     */
    disabled?: boolean;
  }>(),
  { areAllSelected: false, areSomeSelected: false, disabled: false },
);

const emit = defineEmits<{
  (e: 'onSelectAll', payload: boolean): void;
  (e: 'onBulkAction', action: ApplicationBulkActions): void;
}>();

const isOpen = ref(false);
const isChecked = computed(() => props.areAllSelected || props.areSomeSelected);
const isMinusCheck = computed(() => !props.areAllSelected && props.areSomeSelected);
const isDisabled = computed(() => props.disabled || props.isSendingBulkAction || !isChecked.value);

const handleUpdateModelValue = (value: boolean) => {
  if (props.disabled) return;
  isOpen.value = value;
};

const handleCheckSelection = () => {
  if (props.disabled || props.isSendingBulkAction) return;
  emit('onSelectAll', !isChecked.value);
};

// An already-open menu overlay lives outside any suspended wrapper — close it
// the moment the control is disabled.
watch(
  () => props.disabled,
  (disabled) => {
    if (disabled) isOpen.value = false;
  },
);
</script>

<template>
  <div :class="{ 'opacity-50': isDisabled }">
    <span @click.stop="handleCheckSelection" class="cursor-pointer">
      <Checkbox
        :model-value="isChecked"
        :is-minus-check="isMinusCheck"
        class="mb-[-2px] mr-2 inline-block h-4 w-4"
      />
    </span>
    <v-menu
      :model-value="isOpen"
      offset-y
      location="bottom"
      transition="slide-y-transition"
      @update:model-value="handleUpdateModelValue"
      :disabled="isDisabled"
    >
      <template #activator="{ props: activatorProps }">
        <button
          v-bind="activatorProps"
          class="-ml-1"
          :disabled="disabled"
          data-test="bulk-options-activator"
        >
          <ChevronDownIcon
            class="transition-all"
            :class="{
              'rotate-180': isOpen,
              'text-tint-80': !isChecked,
              'text-shade-900': isChecked,
            }"
          />
        </button>
      </template>
      <div class="menu-card -ml-5 mt-0.5">
        <v-list>
          <v-list-item @click="emit('onBulkAction', ApplicationBulkActions.BULK_MESSAGE)">
            <v-list-item-title>Send a message</v-list-item-title>
          </v-list-item>
          <v-list-item @click="emit('onBulkAction', ApplicationBulkActions.MOVE_TO_REVIEWED)">
            <v-list-item-title>Move to Reviewed</v-list-item-title>
          </v-list-item>
          <v-list-item @click="emit('onBulkAction', ApplicationBulkActions.MOVE_TO_REJECTED)">
            <v-list-item-title>Reject</v-list-item-title>
          </v-list-item>
          <v-list-item
            @click="emit('onBulkAction', ApplicationBulkActions.MOVE_TO_REJECTED_W_REASON)"
          >
            <v-list-item-title>Reject w/ reason</v-list-item-title>
          </v-list-item>
          <v-list-item @click="emit('onBulkAction', ApplicationBulkActions.EXPORT_CANDIDATES)">
            <v-list-item-title>Export</v-list-item-title>
          </v-list-item>
          <v-list-item @click="emit('onBulkAction', ApplicationBulkActions.ADD_TO_ANOTHER_JOB)">
            <v-list-item-title>Add to another job</v-list-item-title>
          </v-list-item>
        </v-list>
      </div>
    </v-menu>
  </div>
</template>

<style scoped>
.v-list-item .v-list-item-title {
  @apply text-xs !important;
}
</style>
